import { useState } from "react";

import { useSession } from "@/src/contexts/AuthContext";
import { useApiClient } from "@/src/hooks/useApiClient";
import { useAppToast } from "@/src/hooks/useAppToast";
import { useTranslations } from "@/src/i18n/i18n";
import type { ApiErrorData } from "@/src/libs/ApiError";
import { ApiError } from "@/src/libs/ApiError";
import { ADMIN_ROLE, type SignInBody } from "@/src/pages/SignIn/signIn.type";

/**
 * Connexion à l'admin.
 *
 * Modèle de hook de mutation à reprendre partout : `onSuccess` / `onError` de
 * React Query, jamais un `useEffect` sur `[isSuccess, isError]`.
 */
export function useSignIn() {
  const { setSession } = useSession();
  const { showApiError, showError } = useAppToast();
  const t = useTranslations("signIn");
  const $api = useApiClient();

  /** Erreurs champ par champ, à afficher sous les `Input` du formulaire. */
  const [signInErrors, setSignInErrors] = useState<ApiErrorData>([]);

  const { mutate, isPending } = $api.useMutation("post", "/api/v1/auth", {
    onSuccess: (response) => {
      setSignInErrors([]);

      // Le serveur renvoie 403 sur les routes d'admin, mais rien n'empêche un
      // compte ordinaire de se connecter : on refuse ici plutôt que d'ouvrir
      // une interface dont chaque appel échouerait.
      if (!response.data.roles.includes(ADMIN_ROLE)) {
        showError(t("denied-title"), t("denied-description"));
        return;
      }

      setSession(response.data);
    },
    onError: (error) => {
      // `errorData` porte les erreurs champ par champ, exploitées par le
      // formulaire ; le toast ne dit que l'échec global.
      setSignInErrors(ApiError.from(error)?.errorData ?? []);
      showApiError(t("failed-title"), error);
    },
  });

  const signIn = (body: SignInBody) => {
    mutate({ body });
  };

  const removeSignInError = (field: string) => {
    setSignInErrors((prev) => prev?.filter((e) => e.field !== field) ?? []);
  };

  return { signIn, isPending, signInErrors, removeSignInError };
}
